<template>
  <el-dialog
    v-model="isDialogVisibleUpload"
    title="Upload Slip"
    width="500"
    align-center
    :close-on-click-modal="false"
    @close="handleDialogClose"
  >
    <el-upload
      :show-file-list="false"
      :on-change="uploadChange"
      :auto-upload="false"
      accept="image/jpeg, image/png"
      drag
      @paste="handlePaste"
    >
      <el-button slot="trigger" size="small" type="primary">
        Select File
      </el-button>
    </el-upload>

    <div slot="tip" class="el-upload__tip">
      The file size must not exceed 2 MB
    </div>

    <div class="image-preview" v-if="previewUrl">
      <div class="text-end w-100">
        <button @click="handleDelete">
          <SvgSprite
            name="custom-trash"
            style="width: 22px; height: 22px; color: red"
          />
        </button>
      </div>
      <img :src="previewUrl" class="w-100" alt="Preview" />
    </div>

    <template #footer>
      <div class="dialog-footer">
        <el-button variant="flat" type="danger" @click="handleDialogClose"
          >Cancel</el-button
        >
        <el-button type="primary" @click="submitUpload">Upload</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts" setup>
import { defineProps, defineEmits, ref, inject, computed } from "vue";
import ApiService from "@/core/services/Transaction.ts";
import { LoadingService } from "@/core/plugins/loading";
import { ElNotification } from "element-plus";
import SvgSprite from "@/components/shared/SvgSprite.vue";

const $loading = inject("$loading") as LoadingService;

const props = defineProps({
  modelValue: Boolean,
  selectedOrder: Object,
});

const emit = defineEmits();

const isDialogVisibleUpload = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit("update:modelValue", value);
  },
});

const previewUrl = ref("");
const fileList = ref<any[]>([]);

const handleDialogClose = () => {
  previewUrl.value = "";
  fileList.value = [];
  emit("update:modelValue", false);
};

const handlePaste = async (event: ClipboardEvent) => {
  const clipboardItems = event.clipboardData?.items;
  if (!clipboardItems) return;

  for (const item of clipboardItems) {
    if (item.type.startsWith("image")) {
      const file = item.getAsFile();
      if (file) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          previewUrl.value = e.target.result;
          fileList.value = [file];
        };
        reader.readAsDataURL(file);
      }
    }
  }
};

document.addEventListener("paste", (event) => {
  handlePaste(event, fileList);
});

const uploadChange = (file: any) => {
  if (!file.raw) return;

  const reader = new FileReader();
  let isValidFile = true;

  if (!["image/jpeg", "image/png"].includes(file.raw.type)) {
    ElNotification({
      title: "Error",
      message: "The file must be JPG or PNG only!",
      type: "error",
    });
    isValidFile = false;
    return;
  }

  if (file.raw.size > 2 * 1024 * 1024) {
    ElNotification({
      title: "Error",
      message: "The file size must not exceed 2 MB!",
      type: "error",
    });
    isValidFile = false;
    return;
  }

  if (isValidFile) {
    reader.onload = (event) => {
      if (event.target?.result) {
        fileList.value = [file.raw];
        previewUrl.value = event.target.result as string;
      }
    };
    reader.readAsDataURL(file.raw);
  }
};

const handleDelete = () => {
  previewUrl.value = "";
  fileList.value = [];
};

const submitUpload = async () => {
  if (!fileList.value.length) {
    ElNotification({
      title: "Error",
      message: "Please select a file to upload.",
      type: "error",
    });
    return;
  }

  const file = fileList.value[0];
  const formData = new FormData();
  formData.append("slip", file);
  formData.append("order_id", props.selectedOrder.order_id);
  $loading.startLoading();

  const response = await ApiService.uploadSlip(formData);
  if (response.data.slip) {
    ElNotification({
      title: "Success",
      message: response.data.message,
      type: "success",
    });
    emit("submit-upload", response.data);
    handleDialogClose();
  } else {
    ElNotification({
      title: "Error",
      message: response?.error || "Unknown error",
      type: "error",
    });
  }
  $loading.endLoading();
};
</script>
